import { memo, useCallback, useMemo, type ChangeEvent, type FC } from "react";
import { Radio } from "antd";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import MiniSpinner from "src/components/reusable/Spinner/MiniSpinner";
import ErrorFallback from "src/components/reusable/ErrorFallback/ErrorFallback";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import { formatNumber } from "src/utils/numberFormat";
import CampaignStepCard from "../../../../shared/CampaignStepCard";
import CampaignPaginationFooter from "../../../../shared/CampaignPaginationFooter";
import SelectedTags from "../components/SelectedTags";
import { VISIBLE_CATEGORY_TAGS } from "../AiCampaignConstants";
import { formatCategoryName } from "../AiCampaignUtils";
import type { ProductCategoriesState } from "../hooks/UseProductCategories";

const STEP_KEY = "campaignFlow.aiWizard.productCategories";

interface ProductCategoriesStepProps {
  selectedCategories: string[];
  categoriesState: ProductCategoriesState;
  maxSelection?: number;
}

const ProductCategoriesStep: FC<ProductCategoriesStepProps> = ({
  selectedCategories,
  categoriesState,
  maxSelection,
}) => {
  const { t, i18n } = useTranslation();
  const {
    categories,
    pageCategories,
    selectedSet,
    isLoading,
    hasError,
    searchTerm,
    currentPage,
    totalPages,
    isAllOnScreenSelected,
    isAllSelected,
    isLimitReached,
    loadCategories,
    toggleCategory,
    toggleAllOnScreen,
    toggleAll,
    updateSearchTerm,
    goToPreviousPage,
    goToNextPage,
  } = categoriesState;

  const selectedTagItems = useMemo(
    () => selectedCategories.map(value => ({ value, label: formatCategoryName(value) })),
    [selectedCategories]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => updateSearchTerm(event.target.value),
    [updateSearchTerm]
  );

  const hasLimit = maxSelection !== undefined;
  const limitMessage = t(`${STEP_KEY}.limitReached`, {
    count: formatNumber(maxSelection ?? 0, {}, i18n.language),
  });

  const renderCategories = () => {
    if (isLoading) {
      return <MiniSpinner />;
    }
    if (hasError) {
      return <ErrorFallback onRetry={loadCategories} />;
    }
    if (!pageCategories.length) {
      return <p className="product-categories__empty">{t(`${STEP_KEY}.noResults`)}</p>;
    }
    return (
      <div className="product-categories__grid">
        {pageCategories.map(category => {
          const isSelected = selectedSet.has(category);
          const isLocked = isLimitReached && !isSelected;
          return (
            <StyledTooltip key={category} content={isLocked ? limitMessage : undefined}>
              <button
                type="button"
                aria-pressed={isSelected}
                aria-disabled={isLocked}
                className={`product-categories__item ${
                  isSelected ? "product-categories__item--selected" : ""
                } ${isLocked ? "product-categories__item--disabled" : ""}`}
                onClick={() => toggleCategory(category)}
              >
                {formatCategoryName(category)}
              </button>
            </StyledTooltip>
          );
        })}
      </div>
    );
  };

  return (
    <CampaignStepCard
      title={t(`${STEP_KEY}.title`)}
      subtitle={
        hasLimit
          ? t(`${STEP_KEY}.subtitleLimited`, {
              count: formatNumber(maxSelection, {}, i18n.language),
            })
          : t(`${STEP_KEY}.subtitle`)
      }
      footer={
        <CampaignPaginationFooter
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={goToPreviousPage}
          onNext={goToNextPage}
        />
      }
    >
      <StyledTextInput
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={t(`${STEP_KEY}.searchPlaceholder`)}
        prefix={<Search size={16} aria-hidden />}
        className="campaign-flow__input"
        aria-label={t(`${STEP_KEY}.searchPlaceholder`)}
      />

      {!hasLimit && (
        <div className="product-categories__bulk-actions">
          <Radio
            checked={isAllOnScreenSelected}
            onClick={toggleAllOnScreen}
            disabled={!pageCategories.length}
          >
            {t(`${STEP_KEY}.selectAllOnScreen`, {
              count: formatNumber(pageCategories.length, {}, i18n.language),
            })}
          </Radio>
          <Radio checked={isAllSelected} onClick={toggleAll} disabled={!categories.length}>
            {t(`${STEP_KEY}.selectAllCategories`, {
              count: formatNumber(categories.length, {}, i18n.language),
            })}
          </Radio>
        </div>
      )}

      <SelectedTags
        items={selectedTagItems}
        visibleCount={VISIBLE_CATEGORY_TAGS}
        onRemove={toggleCategory}
      />

      {renderCategories()}
    </CampaignStepCard>
  );
};

export default memo(ProductCategoriesStep);
